import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, Save, X, Bot, BookOpen, Search, ToggleLeft, ToggleRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';

interface KnowledgeEntry {
  id: string;
  category: string;
  title: string;
  content: string;
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

const DEFAULT_CATEGORIES = ['FAQ', 'Sonderangebote', 'Allgemein', 'Technisch', 'Preise', 'Services'];

export const BotManagement = () => {
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<KnowledgeEntry | null>(null);
  const [formData, setFormData] = useState({
    category: 'Allgemein',
    title: '',
    content: '',
    is_active: true,
    priority: 0,
  });
  const { toast } = useToast();

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bot_knowledge')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error loading knowledge entries:', error);
      toast({
        title: 'Fehler',
        description: 'Wissensdatenbank konnte nicht geladen werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingEntry(null);
    setFormData({
      category: 'Allgemein',
      title: '',
      content: '',
      is_active: true,
      priority: 0,
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (entry: KnowledgeEntry) => {
    setEditingEntry(entry);
    setFormData({
      category: entry.category,
      title: entry.title,
      content: entry.content,
      is_active: entry.is_active,
      priority: entry.priority,
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast({
        title: 'Fehler',
        description: 'Titel und Inhalt sind erforderlich.',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (editingEntry) {
        const { error } = await supabase
          .from('bot_knowledge')
          .update({
            category: formData.category,
            title: formData.title,
            content: formData.content,
            is_active: formData.is_active,
            priority: formData.priority,
          })
          .eq('id', editingEntry.id);

        if (error) throw error;
        toast({ title: 'Erfolg', description: 'Eintrag aktualisiert.' });
      } else {
        const { error } = await supabase.from('bot_knowledge').insert({
          category: formData.category,
          title: formData.title,
          content: formData.content,
          is_active: formData.is_active,
          priority: formData.priority,
        });

        if (error) throw error;
        toast({ title: 'Erfolg', description: 'Neuer Eintrag erstellt.' });
      }

      setIsDialogOpen(false);
      loadEntries();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast({
        title: 'Fehler',
        description: 'Eintrag konnte nicht gespeichert werden.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Eintrag wirklich löschen?')) return;

    try {
      const { error } = await supabase.from('bot_knowledge').delete().eq('id', id);
      if (error) throw error;
      toast({ title: 'Erfolg', description: 'Eintrag gelöscht.' });
      loadEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast({
        title: 'Fehler',
        description: 'Eintrag konnte nicht gelöscht werden.',
        variant: 'destructive',
      });
    }
  };

  const toggleActive = async (entry: KnowledgeEntry) => {
    try {
      const { error } = await supabase
        .from('bot_knowledge')
        .update({ is_active: !entry.is_active })
        .eq('id', entry.id);

      if (error) throw error;
      loadEntries();
    } catch (error) {
      console.error('Error toggling entry:', error);
    }
  };

  const categories = [...new Set([...DEFAULT_CATEGORIES, ...entries.map((e) => e.category)])];

  const filteredEntries = entries.filter((entry) => {
    const matchesSearch =
      entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || entry.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const stats = {
    total: entries.length,
    active: entries.filter((e) => e.is_active).length,
    categories: categories.length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Bot className="h-6 w-6 text-cyan-600" />
            Bot-Wissensdatenbank
          </h2>
          <p className="text-slate-600 mt-1">
            Verwalten Sie das Wissen, auf das der KI-Bot zugreifen kann.
          </p>
        </div>
        <Button onClick={openCreateDialog} className="bg-cyan-600 hover:bg-cyan-700">
          <Plus className="h-4 w-4 mr-2" />
          Neuer Eintrag
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Gesamt</p>
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              </div>
              <BookOpen className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Aktiv</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <ToggleRight className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Kategorien</p>
                <p className="text-2xl font-bold text-blue-600">{stats.categories}</p>
              </div>
              <BookOpen className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Kategorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kategorien</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Lade Daten...</div>
          ) : filteredEntries.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {searchTerm || filterCategory !== 'all'
                ? 'Keine Einträge gefunden.'
                : 'Noch keine Einträge vorhanden. Erstellen Sie den ersten Eintrag!'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Kategorie</TableHead>
                  <TableHead>Titel</TableHead>
                  <TableHead className="hidden md:table-cell">Inhalt</TableHead>
                  <TableHead>Priorität</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <button
                        onClick={() => toggleActive(entry)}
                        className={`p-1 rounded ${
                          entry.is_active ? 'text-green-600' : 'text-slate-400'
                        }`}
                      >
                        {entry.is_active ? (
                          <ToggleRight className="h-5 w-5" />
                        ) : (
                          <ToggleLeft className="h-5 w-5" />
                        )}
                      </button>
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-slate-100 rounded text-xs font-medium">
                        {entry.category}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{entry.title}</TableCell>
                    <TableCell className="hidden md:table-cell max-w-xs truncate text-slate-600">
                      {entry.content}
                    </TableCell>
                    <TableCell>{entry.priority}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(entry)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Eintrag bearbeiten' : 'Neuer Wissenseintrag'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Kategorie</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Titel / Frage</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="z.B. 'Welche Zahlungsarten gibt es?'"
              />
            </div>
            <div>
              <Label>Inhalt / Antwort</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Die Antwort oder Information, die der Bot geben soll..."
                rows={5}
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label>Priorität (höher = wichtiger)</Label>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })
                  }
                  min={0}
                  max={100}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
                <Label>Aktiv</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              <X className="h-4 w-4 mr-2" />
              Abbrechen
            </Button>
            <Button onClick={handleSave} className="bg-cyan-600 hover:bg-cyan-700">
              <Save className="h-4 w-4 mr-2" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
